import { useEffect, useState } from "react";
import ModalBackdrop from "./ui/ModalBackdrop";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Step {
  emoji: string;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    emoji: "📧",
    title: "Your case has a unique forwarding address",
    body: "Forward or CC any emails to it and they'll be filed here automatically — sender, subject, date and body text all extracted. Attachments arrive as their own records.",
  },
  {
    emoji: "📋",
    title: "Paste screenshots and text with ⌘V",
    body: "On the Records tab, pressing ⌘V (or Ctrl+V) with an image in your clipboard files it as a new record. Text pastes become plain-text records.",
  },
  {
    emoji: "🔍",
    title: "Use Interrogate to ask questions",
    body: "Once records are in, open Interrogate to ask questions, get a summary, or draft a formal letter or complaint. Everything is grounded in the records on file — no invented dates or quotes.",
  },
];

export default function Onboarding({ open, onClose }: Props) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const current = STEPS[step]!;
  const isLast = step === STEPS.length - 1;

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="onboarding-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="onboarding-steps" aria-hidden>
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboarding-dot${i === step ? " active" : ""}`}
            />
          ))}
        </div>

        <div className="onboarding-emoji" aria-hidden>
          {current.emoji}
        </div>
        <h2 id="onboarding-title" className="onboarding-title">
          {current.title}
        </h2>
        <p className="onboarding-body">{current.body}</p>

        <div className="onboarding-actions">
          <button type="button" className="link-btn" onClick={onClose}>
            Skip
          </button>
          <div className="onboarding-nav">
            {step > 0 && (
              <button type="button" onClick={() => setStep((s) => s - 1)}>
                Back
              </button>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (isLast) onClose();
                else setStep((s) => s + 1);
              }}
            >
              {isLast ? "Got it" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
